import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { useAuth } from "contexts/AuthContext";
import { RootState } from "redux/reducers";
import { sendMedSKUSampleRequest } from "redux/actions/admin";
import { clearErrors } from "redux/actions/errors";

import closeIcon from "assets/icons/close-black.svg";

import TextAreaInput from "./TextAreaInput";
import StandardButton from "components/Common/buttons/StandardButton";

type MedSKURequestFormProps = {
  setToggleModal: Function;
};

export const MedSKURequestForm: React.FC<MedSKURequestFormProps> = ({ setToggleModal }) => {
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const errors = useSelector((state: RootState) => state.errors);

  const [formData, setFormData] = useState({
    name: currentUser.name,
    email: currentUser.email || "",
    phone: currentUser.phone || "",
    company: currentUser.company || "",
    title: currentUser.title || "",
    message: "",
  });

  useEffect(() => {
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearErrors());

    const userMessage = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone || "n/a",
      title: formData.title || "n/a",
      company: formData.company,
      category: "MedSKU",
      message: formData.message || "No message provided",
      subject: "[URGENT] MedSKU Request",
    };

    /**
     * The reason why we call this function instead of sendContactUsMessage 
     * is because we want to direct the email directly to the sales team
     * instead of having to go through support
     */
    dispatch(sendMedSKUSampleRequest(userMessage, setToggleModal));
  };

  return (
    <Container onSubmit={handleSubmit}>
      <Header>
        <div>Request MedSKU Sample</div>
        <CloseButton
          onClick={() => setToggleModal()}
          type="button"
        >
          <img src={closeIcon} alt="close icon" />
        </CloseButton>
      </Header>

      <FormGrid>
        <FormElement fullWidth>
          <Label>Full Name <Asterisk>*</Asterisk></Label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </FormElement>

        <FormElement fullWidth>
          <Label>Email <Asterisk>*</Asterisk></Label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          {errors.email && <ErrorText>{errors.email}</ErrorText>}
        </FormElement>

        <FormElement fullWidth={false}>
          <Label>Phone</Label>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
        </FormElement>

        <FormElement fullWidth={false}>
          <Label>Title</Label>
          <Input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
          />
        </FormElement>

        <FormElement fullWidth>
          <Label>Company <Asterisk>*</Asterisk></Label>
          <Input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            required
          />
        </FormElement>

        <FormElement fullWidth>
          <Label>Message</Label>
          <TextAreaInput
            name="message"
            placeholder="Tell us about your needs..."
            onChange={handleChange}
            value={formData.message}
            error={errors.message}
          />
        </FormElement>
      </FormGrid>

      <ButtonContainer>
        <RequestButton type="submit">
          Request Sample
        </RequestButton>
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.form`
  padding: 2.5rem;
  width: 50rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.5em;
  font-weight: 600;
  margin-bottom: 2rem;
  color: ${(props) => props.theme.colors.idataDarkBlue};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FormElement = styled.div<{ fullWidth?: boolean }>`
  grid-column: ${(props) => (props.fullWidth ? "1 / -1" : "auto")};
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 1rem;
  color: ${(props) => props.theme.colors.idataDarkBlue};
`;

const Asterisk = styled.label`
  color: #ce5548; 
`;

const Input = styled.input`
  padding: 1rem 1rem;
  border: 1px solid #ddd;
  border-radius: 0.5rem;
  transition: border-color 0.2s ease;
  font-size: 1.75rem;

  &:-webkit-autofill,
  &:-webkit-autofill:hover, 
  &:-webkit-autofill:focus, 
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px white inset !important;
    -webkit-text-fill-color: ${(props) => props.theme.colors.idataDarkBlue} !important;
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.idataDarkBlue};
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }
`;

const RequestButton = styled(StandardButton)`
  width: 100%;
  padding: 1.2rem 2rem;
  font-size: 1.75rem;
  background: ${(props) => props.theme.colors.idataDarkBlue};
  color: white;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${(props) => props.theme.colors.idataDarkBlue};
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ErrorText = styled.span`
  color: #e74c3c;
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

export default MedSKURequestForm;